import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { DataTable, Notice, StatusPill } from './patterns';
import type { DataTableColumn, StatusVariant } from './patterns';
import { formatDate, formatDateTime } from '../dateFormat';

interface SyncLogEntry {
  _id: string;
  orchardName?: string;
  userName?: string;
  scoutingRecordDate?: string;
  syncTimestamp?: string;
  status: 'success' | 'failed' | 'quarantined';
  errors?: string;
}

interface SyncLogResponse {
  logs: SyncLogEntry[];
  total: number;
  page: number;
  limit: number;
}

const PAGE_SIZE = 25;

const statusPillVariant = (status: string): StatusVariant => {
  if (status === 'success') return 'ok';
  if (status === 'failed') return 'danger';
  if (status === 'quarantined') return 'warn';
  return 'info';
};

const SyncLogTable: React.FC = () => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<SyncLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLogs = useCallback(async (currentPage: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get<SyncLogResponse>('/api/v1/settings/sync-logs', {
        params: { page: currentPage, limit: PAGE_SIZE },
        withCredentials: true,
      });
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load sync logs.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs(page);
  }, [fetchLogs, page]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const columns: DataTableColumn<SyncLogEntry>[] = [
    {
      key: 'orchard',
      label: t('syncLog.orchardHeader'),
      render: (log) => log.orchardName || '—',
    },
    {
      key: 'scouter',
      label: t('syncLog.scouterHeader'),
      render: (log) => log.userName || '—',
    },
    {
      key: 'recordDate',
      label: t('syncLog.recordDateHeader'),
      render: (log) =>
        log.scoutingRecordDate
          ? formatDate(log.scoutingRecordDate)
          : '—',
    },
    {
      key: 'syncedAt',
      label: t('syncLog.syncedAtHeader'),
      render: (log) =>
        log.syncTimestamp ? formatDateTime(log.syncTimestamp) : '—',
    },
    {
      key: 'status',
      label: t('syncLog.statusHeader'),
      render: (log) => (
        <StatusPill variant={statusPillVariant(log.status)}>{log.status}</StatusPill>
      ),
    },
    {
      key: 'errors',
      label: t('syncLog.errorsHeader'),
      render: (log) => (
        <span className="sync-log-table__errors">{log.errors || '—'}</span>
      ),
    },
  ];

  return (
    <div className="sync-log-table">
      <div className="sync-log-table__header">
        <h3 className="sync-log-table__heading">{t('syncLog.heading')}</h3>
        <button
          type="button"
          className="pat-btn pat-btn--outline"
          onClick={() => fetchLogs(page)}
        >
          <i className="bi bi-arrow-clockwise" aria-hidden="true" /> {t('syncLog.refresh')}
        </button>
      </div>

      {error && (
        <Notice tone="danger">
          {error}
        </Notice>
      )}

      {loading ? (
        <div className="sync-log-table__loading">{t('syncLog.loading')}</div>
      ) : logs.length === 0 ? (
        <Notice tone="info">
          {t('syncLog.noLogs')}
        </Notice>
      ) : (
        <>
          <DataTable<SyncLogEntry>
            columns={columns}
            rows={logs}
            rowKey={(log) => log._id}
          />

          {totalPages > 1 && (
            <div className="sync-log-table__pagination">
              <small className="sync-log-table__pagination-summary">
                {t('syncLog.pagination', { page, totalPages, total })}
              </small>
              <div className="sync-log-table__pagination-actions">
                <button
                  type="button"
                  className="pat-btn pat-btn--outline"
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  {t('syncLog.previous')}
                </button>
                <button
                  type="button"
                  className="pat-btn pat-btn--outline"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  {t('syncLog.next')}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SyncLogTable;
